import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import DriverAssignModal from "@/components/DriverAssignModal";
import RoutesEditor from "@/components/RoutesEditor";
import CustomDatePicker, { CustomTimePicker } from "@/components/DateTimePicker";
import isTrainStation from "@/utils/isTrainStation";
import { Plus as PlusIcon } from "@phosphor-icons/react";
import PhoneInput from "@/components/PhoneInput";
import { validatePhone } from "@/utils/phoneValidation";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate, fmtTime, combineDateTime, arabicDayOfWeek } from "@/utils/date";
import { translateLocation } from "@/utils/locationI18n";
import { fmtNum, fmtRiyal, toEnglishDigits } from "@/utils/toEnglishDigits";
import { formatPhoneDisplay, formatPhoneForWhatsApp } from "@/utils/phoneFormat";
import { buildClientWhatsAppLink } from "@/utils/whatsapp";
import { getBookingAirline, getBookingFlightNumber } from "@/utils/airline";
import { buildCapacityMap, loadCachedCapacityMap, saveCachedCapacityMap } from "@/utils/seats";
import {
  ArrowRight,
  Phone,
  WhatsappLogo,
  User as UserIcon,
  Car as CarIcon,
  MapPin,
  Calendar,
  Clock,
  Money,
  Printer,
  PencilSimple,
  ArrowUp,
  XCircle,
  CheckCircle,
  Note,
  ArrowsClockwise,
  Warning,
  IdentificationBadge,
  Airplane,
} from "@phosphor-icons/react";

// 2026-02-24 §StatusFlow restored: display the REAL status. `pending`
// = orange قيد المراجعة, `confirmed` = green مؤكد, `rejected`/`cancelled`
// = red. Admin buttons below (Confirm / Reject) act on `pending` rows.
const STATUS_META = {
  pending:   { ar: "قيد المراجعة", color: "text-[#F57F17]", bg: "bg-[#F57F17]/15", border: "border-[#F57F17]/40" },
  confirmed: { ar: "مؤكد",        color: "text-[#22C55E]", bg: "bg-[#22C55E]/15", border: "border-[#22C55E]/40" },
  completed: { ar: "مكتمل",       color: "text-[#22C55E]", bg: "bg-[#22C55E]/15", border: "border-[#22C55E]/40" },
  cancelled: { ar: "ملغى",        color: "text-destructive", bg: "bg-destructive/15", border: "border-destructive/40" },
  rejected:  { ar: "مرفوض",       color: "text-destructive", bg: "bg-destructive/15", border: "border-destructive/40" },
};

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-primary/10 last:border-0 gap-3">
      <span className="text-white/55 text-sm shrink-0">{label}</span>
      <span className={`text-white text-sm text-left ${mono ? "num" : ""}`} dir={mono ? "ltr" : "rtl"}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-2xl bg-card border border-primary/20 p-5 fadeup">
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-primary/15">
        <div className="flex items-center gap-2 text-primary font-bold">
          {Icon && <Icon size={18} weight="duotone" />}
          <span>{title}</span>
        </div>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ─── Timeline ────────────────────────────────────────────────────────
function Timeline({ b }) {
  const { t } = useTranslation();
  const steps = useMemo(() => {
    const s = b.status;
    const cancelled = s === "cancelled" || s === "rejected";
    return [
      { key: "created",   label: t("bookingDetails.timeline.created"),     at: b.created_at,    done: true },
      { key: "confirmed", label: t("bookingDetails.timeline.confirmed"),   at: b.confirmed_at,  done: !!b.confirmed_at || ["confirmed","completed"].includes(s) },
      { key: "assigned",  label: t("bookingDetails.timeline.assigned"),    at: b.assigned_at,   done: !!(b.driver_id || b.external_driver_name) },
      { key: "completed", label: t("bookingDetails.timeline.completed"),   at: b.completed_at,  done: s === "completed" },
      cancelled && { key: "cancelled", label: s === "rejected" ? t("bookingDetails.timeline.rejected") : t("bookingDetails.timeline.cancelled"), at: b.cancelled_at, done: true, danger: true },
    ].filter(Boolean);
  }, [b, t]);

  const currentIdx = steps.findIndex((x) => !x.done);
  return (
    <ol className="relative pr-6" data-testid="booking-timeline">
      <div className="absolute right-2 top-2 bottom-2 w-px bg-primary/20" />
      {steps.map((step, i) => {
        const isCurrent = i === currentIdx;
        const dotColor = step.danger
          ? "bg-destructive"
          : step.done
            ? "bg-primary"
            : isCurrent
              ? "bg-primary animate-pulse"
              : "bg-white/20";
        return (
          <li key={step.key} className="relative py-2.5 pr-3">
            <span
              className={`absolute right-0 top-4 w-3 h-3 rounded-full border-2 border-card ${dotColor}`}
              style={{ boxShadow: step.done ? "0 0 8px rgba(201,169,97,0.6)" : "none" }}
            />
            <div className={`text-sm font-semibold ${
              step.danger ? "text-destructive" : step.done ? "text-white" : "text-white/45"
            }`}>
              {step.done ? "✓" : isCurrent ? "•" : "○"} {step.label}
            </div>
            {step.at && (
              <div className="text-xs text-white/45 num mt-0.5" dir="ltr">
                {fmtDate(step.at)} · {fmtTime(step.at)}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─── PDF Invoice generator ───────────────────────────────────────────
async function generateInvoicePDF(b) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const PAD = 40;

  // Header band (navy)
  doc.setFillColor(10, 25, 54);
  doc.rect(0, 0, W, 90, "F");

  // Try to draw the logo (fetched as data URL)
  try {
    const r = await fetch("/logo-gold.png");
    const blob = await r.blob();
    const dataUrl = await new Promise((res) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.readAsDataURL(blob);
    });
    doc.addImage(dataUrl, "PNG", PAD, 15, 60, 60);
  } catch {
    /* logo optional — invoice generation must not fail if fetch errors */
  }

  doc.setTextColor(201, 169, 97);
  doc.setFontSize(20);
  doc.setFont(undefined, "bold");
  doc.text("MARASIM ALZUWAR", W - PAD, 35, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(220, 220, 220);
  doc.text("Luxury Transport Services", W - PAD, 52, { align: "right" });
  doc.setFontSize(9);
  doc.text("Booking Invoice", W - PAD, 70, { align: "right" });

  // Invoice meta band
  let y = 115;
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text(`Invoice #: ${toEnglishDigits(b.booking_number || b.id || "")}`, PAD, y);
  y += 16;
  doc.setFont(undefined, "normal");
  doc.setFontSize(10);
  doc.text(`Issue Date: ${toEnglishDigits(new Date().toISOString().slice(0, 10))}`, PAD, y);
  doc.text(`Status: ${b.status || "—"}`, W - PAD, y, { align: "right" });

  // Section: Trip details
  y += 26;
  const sectionTitle = (title) => {
    doc.setFillColor(201, 169, 97);
    doc.rect(PAD, y - 12, W - PAD * 2, 18, "F");
    doc.setTextColor(10, 25, 54);
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text(title, PAD + 8, y + 1);
    y += 20;
  };

  const row = (label, value) => {
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, "bold");
    doc.setFontSize(9.5);
    doc.text(`${label}:`, PAD + 8, y);
    doc.setFont(undefined, "normal");
    doc.setTextColor(40, 40, 40);
    doc.text(toEnglishDigits(String(value ?? "—")), PAD + 120, y, { maxWidth: W - PAD * 2 - 128 });
    y += 15;
  };

  sectionTitle("TRIP DETAILS");
  // Spec 2026-07-23 v3.3 §Time-Sync: routes[0] is the SOURCE OF TRUTH for the
  // header. Legacy `booking.pickup_time` / `booking.trip_time` are only
  // fallbacks for very old records that never got a `routes` array. This
  // fixes MZ-000590 where the DB still had booking.pickup_time="…T16:10"
  // while routes[0].trip_time was correctly "13:10".
  const _r0Pdf = Array.isArray(b.routes) && b.routes.length ? b.routes[0] : null;
  const iso = (_r0Pdf?.trip_date && _r0Pdf?.trip_time)
    ? combineDateTime(_r0Pdf.trip_date, _r0Pdf.trip_time)
    : (b.pickup_time || combineDateTime(b.trip_date, b.trip_time));
  row("Trip date", iso ? toEnglishDigits(fmtDate(iso)) : "—");
  row("Trip time", iso ? toEnglishDigits(fmtTime(iso)) : "—");
  row("From", b.pickup_location);
  row("To", b.dropoff_location || b.destination);
  row("Car type", b.car_type_name || b.car_type);
  row("Passengers", b.passengers);
  {
    const _flightNo = getBookingFlightNumber(b);
    const _airline = getBookingAirline(b);
    if (_flightNo) row("Flight #", `${_airline || ""} ${_flightNo}`.trim());
  }

  y += 6;
  sectionTitle("CLIENT");
  row("Name", b.client_name);
  row("Phone", b.client_phone ? toEnglishDigits(`+${String(b.client_phone).replace(/\D/g, "")}`) : "—");
  if (b.client_phone_alt) row("Alt phone", toEnglishDigits(`+${String(b.client_phone_alt).replace(/\D/g, "")}`));

  if (b.driver_name || b.external_driver_name) {
    y += 6;
    sectionTitle("DRIVER");
    row("Name", b.driver_name || b.external_driver_name);
    if (b.driver_phone || b.external_driver_phone)
      row("Phone", toEnglishDigits(`+${String(b.driver_phone || b.external_driver_phone).replace(/\D/g, "")}`));
    if (b.plate_number) row("Plate", b.plate_number);
    if (b.external_driver_name && !b.driver_id) row("Type", "External");
  }

  y += 6;
  sectionTitle("FINANCIALS");
  row("Total price", toEnglishDigits(`${fmtNum(b.price || 0)} SAR`));
  if (b.driver_wage) row("Driver wage", toEnglishDigits(`${fmtNum(b.driver_wage)} SAR`));
  row("Company net", toEnglishDigits(`${fmtNum((b.price || 0) - (b.driver_wage || 0))} SAR`));

  // Footer
  doc.setFillColor(10, 25, 54);
  doc.rect(0, H - 60, W, 60, "F");
  doc.setTextColor(201, 169, 97);
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text("Thank you for choosing Marasim Alzuwar", W / 2, H - 35, { align: "center" });
  doc.setTextColor(220, 220, 220);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("Luxury transport for pilgrims and visitors", W / 2, H - 20, { align: "center" });

  doc.save(`Marasim-Invoice-${toEnglishDigits(String(b.booking_number || b.id || ""))}.pdf`);
}

// ─── Main page ────────────────────────────────────────────────────────

// One-shot "Recalculate price" button (spec 2026-07-24 v5 §PerBookingRecalc).
// Sits next to "تعديل الحجز" in the header — the admin clicks once and the
// server re-runs the rule-match against the CURRENT `route_rules` for every
// leg. Never wipes a hand-set price with 0 (see backend NoZeroWipe guard).
function RecalcPriceButton({ bookingId, onRecalculated }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/recalculate-price`);
      if (!data.changed) {
        toast.info(
          data.reason === "no_matching_rules"
            ? t("bookingDetails.recalcNoRule")
            : t("bookingDetails.recalcSame"),
        );
      } else {
        toast.success(
          t("bookingDetails.recalcUpdated", {
            oldPrice: fmtNum(data.old_total),
            newPrice: fmtNum(data.new_total),
          }),
        );
        onRecalculated && onRecalculated();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("bookingDetails.recalcError"));
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid="btn-recalc-price"
      title={t("bookingDetails.recalcTooltip")}
      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#4ADE80] hover:bg-[#22C55E]/25 text-sm font-bold disabled:opacity-50"
    >
      {busy ? "…" : "♻️"} {t("bookingDetails.recalcPrice")}
    </button>
  );
}

export default function BookingDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const isAdmin = user?.role === "admin";
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Live capacity map — used to compute the WA passenger count from car type × qty.
  // Seeded from localStorage so the first WA link is correct without waiting on /api/cars.
  const [carsCapacity, setCarsCapacity] = useState(() => loadCachedCapacityMap());
  // Ref to the RoutesEditor so the Timeline card's "add route" button can
  // open the modal remotely.
  const routesEditorRef = useRef(null);
  useEffect(() => {
    api.get("/cars/capacity")
      .catch(() => api.get("/cars"))
      .then((r) => {
        const m = buildCapacityMap(r.data);
        if (Object.keys(m).length > 0) { setCarsCapacity(m); saveCachedCapacityMap(m); }
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // 2026-02-26 §StateLeakFix: when navigating between different bookings
  // (e.g. clicking a booking link while another booking's detail page is
  // already open, or when the URL `id` changes via router state), close
  // every open modal so their internal `useState(booking.*)` initializers
  // never carry over cash / commission values from the PREVIOUS booking.
  // Without this, editing booking B could inherit and overwrite booking B
  // with booking A's cash_from_client + agent_commission — a critical
  // financial data leak. The modals themselves also reinitialize because
  // the parent re-renders with a new `booking` prop, but we still force
  // them closed here as a belt-and-braces guarantee.
  useEffect(() => {
    setAssignOpen(false);
    setEditOpen(false);
    setUpgradeOpen(false);
  }, [id]);

  const cancelBooking = async () => {
    const ok = await confirm({
      title: t("bookingDetails.cancelTitle"),
      message: t("bookingDetails.cancelMessage"),
      confirmText: t("bookingDetails.cancelConfirm"),
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success(t("bookingDetails.cancelSuccess"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const acceptBooking = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/accept`);
      toast.success(t("bookingDetails.confirmSuccess"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const rejectBooking = async () => {
    const ok = await confirm({
      title: "رفض الحجز",
      message: "هل أنت متأكد من رفض هذا الحجز؟",
      confirmText: "رفض",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${id}`, { status: "rejected" });
      toast.success("تم رفض الحجز");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const completeBooking = async () => {
    const ok = await confirm({
      title: t("bookingDetails.completeTitle"),
      message: t("bookingDetails.completeMessage"),
      confirmText: t("bookingDetails.completeConfirm"),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success(t("bookingDetails.completeSuccess"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const printPdf = async () => {
    if (!b) return;
    try {
      // Server-side PDF (professional Navy/Gold design). Fetch with auth then open.
      const res = await api.get(`/bookings/${b.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Try opening in a new tab; fall back to auto-download.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `marasim-${toEnglishDigits(String(b.booking_number || b.id || ""))}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke the blob URL after a short delay to allow the tab to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(t("bookingDetails.invoiceGenerated"));
    } catch {
      toast.error(t("bookingDetails.invoiceError"));
    }
  };

  if (loading || !b) {
    return (
      <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
        <TopBar />
        <main className="max-w-5xl mx-auto p-6 text-primary animate-pulse text-center">{t("bookingDetails.loading")}</main>
      </div>
    );
  }

  const st = STATUS_META[b.status] || STATUS_META.pending;
  // Spec 2026-07-23 v3.3 §Time-Sync: routes[0] is the SOURCE OF TRUTH for the
  // header. Legacy `booking.pickup_time` / `booking.trip_time` are only
  // fallbacks for very old records that never got a `routes` array. This
  // fixes MZ-000590 where the DB still had booking.pickup_time="…T16:10"
  // while routes[0].trip_time was correctly "13:10".
  const _r0 = Array.isArray(b.routes) && b.routes.length ? b.routes[0] : null;
  const iso = (_r0?.trip_date && _r0?.trip_time)
    ? combineDateTime(_r0.trip_date, _r0.trip_time)
    : (b.pickup_time || combineDateTime(b.trip_date, b.trip_time));
  const wageOrZero = b.driver_wage || 0;
  // 2026-02-26 §PerRouteFinancials: total commission / cash comes from
  // sum(routes[].*) when any per-route value exists, otherwise fall back
  // to the legacy booking-level field. Same logic mirrored on the backend
  // (server.py: booking_agent_commission / booking_cash_from_client) and
  // on the bookings table.
  const _perRouteCommission = Array.isArray(b.routes)
    ? b.routes.reduce((s, r) => s + Number(r?.agent_commission || 0), 0)
    : 0;
  const _perRouteCash = Array.isArray(b.routes)
    ? b.routes.reduce((s, r) => s + Number(r?.cash_from_client || 0), 0)
    : 0;
  const effectiveCommission = _perRouteCommission > 0
    ? _perRouteCommission
    : Number(b.agent_commission || 0);
  const effectiveCash = _perRouteCash > 0
    ? _perRouteCash
    : Number(b.cash_from_client || 0);
  const net = (b.price || 0) - wageOrZero - effectiveCommission;

  const driverPhone = b.driver_phone || b.external_driver_phone;
  const driverName = b.driver_name || b.external_driver_name;
  const isExternal = !b.driver_id && !!b.external_driver_name;

  const waLink = (phone, text) => {
    const p = formatPhoneForWhatsApp(phone);
    if (!p) return null;
    return `https://wa.me/${p}?text=${encodeURIComponent(text || "")}`;
  };

  const clientText =
    `مرحباً ${b.client_name || ""}،\n` +
    `نتواصل معك من مراسم الزوار بخصوص حجزك رقم #${b.booking_number || b.id}.`;
  const driverText =
    `مرحباً ${driverName || ""}،\n` +
    `تفاصيل الحجز #${b.booking_number || b.id}:\n` +
    `📅 ${iso ? `${fmtDate(iso)} ${fmtTime(iso)}` : ""}\n` +
    `📍 ${b.pickup_location} ← ${b.dropoff_location || b.destination}\n` +
    `🚗 ${b.car_type_name || b.car_type}`;

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              data-testid="back-btn"
              className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
              title={t("bookingDetails.backBtn")}
            >
              <ArrowRight size={16} weight="bold" className={isEn ? "rotate-180" : ""} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                {t("bookingDetails.bookingNoLabel")} <span className="num text-primary" dir="ltr">#{b.booking_number}</span>
              </h1>
              <div className="text-white/50 text-sm mt-1 flex items-center gap-2">
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full ${st.bg} ${st.color} ${st.border} border font-bold`}
                  data-testid="booking-status-badge"
                >
                  {st.ar}
                </span>
                {b.allow_double_booking && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F57F17]/15 border border-[#F57F17]/40 text-[#F57F17] font-bold inline-flex items-center gap-0.5">
                    <Warning size={9} weight="fill" /> {t("bookingDetails.doubleTrip")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={printPdf}
              data-testid="print-pdf"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/10 border border-primary/40 text-primary hover:bg-primary/20 text-sm font-bold"
            >
              <Printer size={14} weight="bold" /> {t("bookingDetails.invoicePdf")}
            </button>
            {b.customer_email && (
              <span
                data-testid="pdf-auto-sent-badge"
                title={t("bookingDetails.invoiceEmailTooltip", { email: b.customer_email })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/40 text-emerald-300 text-[11px] font-bold"
              >
                📧 {t("bookingDetails.invoiceEmailAuto")}
              </span>
            )}
            {isAdmin && (
              <button
                onClick={() => setEditOpen(true)}
                data-testid="btn-edit-booking"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground border border-primary hover:bg-primary/90 text-sm font-bold shadow-lg shadow-primary/20"
              >
                <PencilSimple size={14} weight="bold" /> {t("bookingDetails.edit")}
              </button>
            )}
            {isAdmin && <RecalcPriceButton bookingId={b.id} onRecalculated={load} />}
            {isAdmin && (
              <button
                onClick={() => setUpgradeOpen(true)}
                data-testid="btn-upgrade-car"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-lg text-black"
                style={{ backgroundColor: "#00E1FF", boxShadow: "0 4px 14px rgba(0,225,255,0.35)" }}
                title={t("bookingDetails.upgrade")}
              >
                <ArrowUp size={14} weight="bold" /> {t("bookingDetails.upgrade")}
              </button>
            )}
            {/* 2026-02-24 §StatusFlow — admin actions on PENDING bookings.
                Click "تأكيد الحجز" → POST /bookings/:id/accept (moves to confirmed).
                Click "رفض الحجز" → PATCH status=rejected. */}
            {isAdmin && b.status === "pending" && (
              <>
                <button
                  onClick={acceptBooking}
                  disabled={busy}
                  data-testid="btn-confirm-booking"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/60 text-[#22C55E] hover:bg-[#22C55E]/25 text-sm font-bold"
                >
                  <CheckCircle size={14} weight="bold" /> تأكيد الحجز
                </button>
                <button
                  onClick={rejectBooking}
                  disabled={busy}
                  data-testid="btn-reject-booking"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-destructive/15 border border-destructive/50 text-destructive hover:bg-destructive/25 text-sm font-bold"
                >
                  <XCircle size={14} weight="bold" /> رفض الحجز
                </button>
              </>
            )}
            {isAdmin && b.status === "confirmed" && (
              <button
                onClick={completeBooking}
                disabled={busy}
                data-testid="complete-booking"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/15 border border-primary/50 text-primary hover:bg-primary/25 text-sm font-bold"
              >
                <CheckCircle size={14} weight="bold" /> {t("bookingDetails.complete")}
              </button>
            )}
            {isAdmin && !["cancelled", "completed", "rejected"].includes(b.status) && (
              <button
                onClick={cancelBooking}
                disabled={busy}
                data-testid="cancel-booking"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-destructive/15 border border-destructive/50 text-destructive hover:bg-destructive/25 text-sm font-bold"
              >
                <XCircle size={14} weight="bold" /> {t("bookingDetails.cancel")}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Trip details */}
          <Card title={t("bookingDetails.cards.trip")} icon={Calendar}>
            <DetailRow label={t("bookingDetails.fields.date")} value={<span className="num" dir="ltr">{iso ? fmtDate(iso) : "—"}{b.trip_date && arabicDayOfWeek(b.trip_date) ? <span className="text-primary mr-2 num-off" dir="rtl"> · {arabicDayOfWeek(b.trip_date)}</span> : null}</span>} />
            <DetailRow label={t("bookingDetails.fields.time")} value={<span className="num" dir="ltr">{iso ? fmtTime(iso) : "—"}</span>} />
            <DetailRow
              label={t("bookingDetails.fields.route")}
              value={
                <span className="inline-flex items-center gap-1 justify-end flex-wrap">
                  <MapPin size={12} className="text-primary" />
                  {translateLocation(b.pickup_location, i18n.language)} <ArrowRight size={11} className={`text-white/40 ${isEn ? "rotate-180" : ""}`} /> {translateLocation(b.dropoff_location || b.destination, i18n.language)}
                </span>
              }
            />
            <DetailRow
              label={t("bookingDetails.fields.carType")}
              value={
                <div className="flex items-center gap-2 justify-end flex-wrap">
                  <span>{b.car_type_name || b.car_type}</span>
                  {b.is_upgraded && b.upgraded_from_car_name_ar && (
                    <span
                      data-testid="upgrade-badge"
                      title={b.upgrade_reason ? `السبب: ${b.upgrade_reason}` : undefined}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold"
                      style={{
                        background: "rgba(0,225,255,0.12)",
                        border: "1px solid #00E1FF",
                        color: "#00E1FF",
                      }}
                    >
                      <ArrowUp size={11} weight="bold" />
                      <span>مُرَقّى من {b.upgraded_from_car_name_ar}</span>
                    </span>
                  )}
                </div>
              }
            />
            <DetailRow label={t("bookingDetails.fields.passengers")} value={<span className="num" dir="ltr">{fmtNum(b.passengers || 0)}</span>} />
            {/* 2026-02-26 §PerRouteFlight-Display: رقم الرحلة وشركة الطيران
                أصبحا يُعرضان داخل بطاقة كل مسار في `RoutesEditor` (سطر
                الطيران الخاص بذلك المسار). عرضهما هنا في القسم العلوي كان
                يخفي الاختلاف بين المسارات — الحجز متعدد المسارات قد يحمل
                رحلات مختلفة على كل ساق. */}
            {b.notes && (
              <div className="mt-3 pt-3 border-t border-primary/10 text-sm">
                <div className="text-white/55 mb-1 inline-flex items-center gap-1">
                  <Note size={12} className="text-primary/70" /> {t("bookingDetails.fields.notes")}
                </div>
                <div className="text-white/85 whitespace-pre-wrap">{b.notes}</div>
              </div>
            )}
          </Card>

          {/* Financials */}
          <Card title={t("bookingDetails.cards.financial")} icon={Money}>
            <DetailRow label={t("bookingDetails.fields.tripPrice")} value={<span className="num text-primary" dir="ltr">{fmtRiyal(b.price || 0)}</span>} />
            <DetailRow label={t("bookingDetails.fields.driverWage")} value={<span className="num text-destructive" dir="ltr">{fmtRiyal(wageOrZero)}</span>} />
            {effectiveCommission > 0 && (
              <DetailRow
                label="عمولة المندوب"
                value={<span data-testid="detail-agent-commission" className="num font-bold" dir="ltr" style={{ color: "#B45309" }}>{fmtRiyal(effectiveCommission)}</span>}
              />
            )}
            {effectiveCash > 0 && (
              <>
                <DetailRow
                  label="عميل كاش"
                  value={<span data-testid="detail-cash-from-client" className="num" dir="ltr" style={{ color: "#3B82F6" }}>{fmtRiyal(effectiveCash)}</span>}
                />
                <DetailRow
                  label="صافي أجرة السائق"
                  value={
                    <span className="num font-bold" dir="ltr" style={{ color: "#F57F17" }}>
                      {fmtRiyal(Math.max(0, wageOrZero - effectiveCash))}
                    </span>
                  }
                />
              </>
            )}
            <div className="flex items-center justify-between py-3 mt-1 bg-primary/10 rounded-lg px-3 border border-primary/25">
              <span className="text-primary font-bold text-sm">صافي الشركة</span>
              <span className="num text-primary font-black text-lg" dir="ltr">{fmtRiyal(net)}</span>
            </div>
            {b.season_label && (
              <div className="mt-3 text-xs text-white/60">
                موسم: <span className="text-primary font-bold">{b.season_label}</span>
                {b.season_multiplier && (
                  <> · معامل: <span className="num text-primary" dir="ltr">×{fmtNum(b.season_multiplier)}</span></>
                )}
              </div>
            )}
          </Card>

          {/* Client */}
          <Card title="العميل" icon={UserIcon}>
            <DetailRow label="الاسم" value={b.client_name} />
            <DetailRow
              label="الجوال"
              mono
              value={
                b.client_phone ? (
                  <span className="inline-flex items-center gap-1.5 justify-end" dir="ltr">
                    <span className="num">{formatPhoneDisplay(b.client_phone)}</span>
                    <a
                      href={`tel:+${String(b.client_phone).replace(/\D/g, "")}`}
                      data-testid="call-client-inline"
                      title="اتصال"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary/40 text-primary hover:bg-primary/10"
                    >
                      <Phone size={11} weight="fill" />
                    </a>
                    <a
                      href={buildClientWhatsAppLink(b, b.client_phone, carsCapacity)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="wa-client-inline"
                      title="واتساب — تفاصيل الحجز"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white"
                    >
                      <WhatsappLogo size={11} weight="fill" />
                    </a>
                  </span>
                ) : "—"
              }
            />
            {b.client_phone_alt && (
              <DetailRow
                label="رقم بديل"
                mono
                value={
                  <span className="inline-flex items-center gap-1.5 justify-end" dir="ltr">
                    <span className="num">{formatPhoneDisplay(b.client_phone_alt)}</span>
                    <a
                      href={`tel:+${String(b.client_phone_alt).replace(/\D/g, "")}`}
                      data-testid="call-client-alt-inline"
                      title="اتصال"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary/40 text-primary hover:bg-primary/10"
                    >
                      <Phone size={11} weight="fill" />
                    </a>
                    <a
                      href={buildClientWhatsAppLink(b, b.client_phone_alt, carsCapacity)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="wa-client-alt-inline"
                      title="واتساب — تفاصيل الحجز"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white"
                    >
                      <WhatsappLogo size={11} weight="fill" />
                    </a>
                  </span>
                }
              />
            )}
            <div className="flex gap-2 pt-3 mt-1">
              {b.client_phone && (
                <>
                  <a
                    href={`tel:+${String(b.client_phone).replace(/\D/g, "")}`}
                    data-testid="call-client"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold"
                  >
                    <Phone size={13} weight="bold" /> اتصال
                  </a>
                  <a
                    href={buildClientWhatsAppLink(b, b.client_phone, carsCapacity)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="wa-client"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366]/15 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/25 text-xs font-bold"
                  >
                    <WhatsappLogo size={13} weight="fill" /> واتساب — إرسال تفاصيل الحجز
                  </a>
                </>
              )}
            </div>
          </Card>

          {/* Driver */}
          <Card
            title="السائق"
            icon={CarIcon}
            action={
              isAdmin && !["cancelled", "completed", "rejected"].includes(b.status) && (
                <button
                  onClick={() => setAssignOpen(true)}
                  data-testid="change-driver-btn"
                  className="text-xs text-primary hover:text-[#E1C484] inline-flex items-center gap-1 font-bold"
                >
                  <ArrowsClockwise size={12} weight="bold" /> {driverName ? "تغيير" : "تعيين"}
                </button>
              )
            }
          >
            {driverName ? (
              <>
                <DetailRow
                  label="الاسم"
                  value={
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      {driverName}
                      {isExternal && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] font-bold">
                          خارجي
                        </span>
                      )}
                    </span>
                  }
                />
                {driverPhone && <DetailRow label="الجوال" mono value={formatPhoneDisplay(driverPhone)} />}
                {b.plate_number && (
                  <DetailRow
                    label="اللوحة"
                    mono
                    value={
                      <span className="inline-flex items-center gap-1 justify-end">
                        <IdentificationBadge size={12} className="text-primary/70" /> {b.plate_number}
                      </span>
                    }
                  />
                )}
                {driverPhone && (
                  <div className="flex gap-2 pt-3 mt-1">
                    <a
                      href={`tel:+${String(driverPhone).replace(/\D/g, "")}`}
                      data-testid="call-driver"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold"
                    >
                      <Phone size={13} weight="bold" /> اتصال
                    </a>
                    <a
                      href={waLink(driverPhone, driverText)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="wa-driver"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366]/15 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/25 text-xs font-bold"
                    >
                      <WhatsappLogo size={13} weight="fill" /> واتساب
                    </a>
                  </div>
                )}
              </>
            ) : (
              <div className="py-6 text-center text-white/40 text-sm">
                <CarIcon size={32} weight="duotone" className="mx-auto mb-2 text-white/25" />
                لم يتم تعيين سائق بعد
              </div>
            )}
          </Card>

          {/* Timeline + Add-Route (side-by-side on desktop, stacked on mobile) */}
          <div className="lg:col-span-2">
            <div
              className={`grid gap-4 ${
                isAdmin ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
              } items-stretch`}
            >
              <Card title="الخط الزمني" icon={Clock}>
                <Timeline b={b} />
              </Card>

              {isAdmin && (
                <button
                  type="button"
                  onClick={() => routesEditorRef.current?.openAdd()}
                  data-testid="add-route-hero-card"
                  className="group relative rounded-2xl overflow-hidden text-right fadeup transition-transform duration-200 hover:-translate-y-1 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  style={{
                    background:
                      "linear-gradient(135deg, #C9A961 0%, #B8963F 100%)",
                    minHeight: 260,
                    color: "#fff",
                  }}
                >
                  <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
                    background:
                      "radial-gradient(circle at 85% 20%, rgba(255,255,255,0.6), transparent 55%)",
                  }} />
                  <div className="relative h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-white/20 border border-white/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <PlusIcon size={30} weight="bold" />
                    </div>
                    <h3 className="text-xl font-extrabold text-white">
                      إضافة مسار جديد
                    </h3>
                    <p className="text-sm text-white/90 leading-relaxed max-w-[280px]">
                      اضغط لإضافة مسار جديد للحجز
                      <br />
                      (السعر يُحسب تلقائياً من قواعد التسعير الحالية)
                    </p>
                    <span
                      className="mt-1 inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold bg-white text-[#B8963F] shadow-md group-hover:brightness-95"
                      data-testid="add-route-hero-cta"
                    >
                      <PlusIcon size={14} weight="bold" /> إضافة مسار الآن
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
          {isAdmin && (
            <RoutesEditor
              ref={routesEditorRef}
              booking={b}
              showAddButton={false}
              onUpdated={(nb) => setB((prev) => ({ ...(prev || {}), ...(nb || {}) }))}
            />
          )}
        </div>
      </main>

      {assignOpen && (
        <DriverAssignModal
          key={`assign-${b.id}`}
          booking={b}
          onClose={() => setAssignOpen(false)}
          onSaved={load}
        />
      )}

      {editOpen && (
        <EditBookingModal
          key={`edit-${b.id}`}
          booking={b}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setB(updated);
            setEditOpen(false);
          }}
        />
      )}

      {upgradeOpen && (
        <UpgradeCarModal
          key={`upgrade-${b.id}`}
          booking={b}
          onClose={() => setUpgradeOpen(false)}
          onSaved={(updated) => {
            setB(updated);
            setUpgradeOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Admin — Full-edit modal (5 sections)
// ============================================================================
function EditBookingModal({ booking, onClose, onSaved }) {
  const [cars, setCars] = useState([]);
  // Multi-car support (spec 2026-07-24 v8 §MultiCarEdit).
  // Legacy bookings stored ONE `car_type` + `quantity` on the top-level
  // booking doc. Modern bookings store a `cars[]` array inside routes[0]
  // (and/or on the booking itself). We normalise on load so the edit form
  // always operates on a `selectedCars` list — each entry {car_type, quantity}.
  const _initialCars = useMemo(() => {
    const src =
      (booking.routes && booking.routes[0] && Array.isArray(booking.routes[0].cars) && booking.routes[0].cars) ||
      (Array.isArray(booking.cars) && booking.cars) ||
      null;
    if (src && src.length) {
      return src.map((c) => ({
        car_type: c.car_type,
        quantity: Number(c.quantity) || 1,
      }));
    }
    // Fallback for very old records with only top-level car_type.
    if (booking.car_type) {
      return [{
        car_type: booking.car_type,
        quantity: Number(booking.quantity) || 1,
      }];
    }
    return [];
  }, [booking]);
  const [selectedCars, setSelectedCars] = useState(_initialCars);
  const [form, setForm] = useState({
    status: booking.status || "pending",
    client_name: booking.client_name || booking.user_name || "",
    client_phone: booking.client_phone || booking.user_phone || "",
    client_phone_2: booking.client_phone_alt || booking.client_phone_2 || "",
    pickup_location: booking.pickup_location || "",
    destination: booking.destination || booking.dropoff_location || "",
    trip_date: booking.trip_date || "",
    trip_time: booking.trip_time || "",
    flight_number: booking.flight_number || "",
    flight_airline: booking.flight_airline || "",
    departure_time_client: booking.departure_time_client || "",
    departure_time_driver: booking.departure_time_driver || "",
    passengers: booking.passengers || 1,
    total_price: booking.total_price != null ? String(booking.total_price) : "",
    cash_from_client: booking.cash_from_client != null ? String(booking.cash_from_client) : "",
    agent_commission: booking.agent_commission != null ? String(booking.agent_commission) : "",
    notes: booking.notes || "",
  });
  const [saving, setSaving] = useState(false);

  // Helpers for +/- car quantity buttons (mirror AddRouteModal UX).
  const carSet = useCallback((car_type, delta) => {
    setSelectedCars((prev) => {
      const idx = prev.findIndex((c) => c.car_type === car_type);
      if (idx === -1) {
        return delta > 0 ? [...prev, { car_type, quantity: 1 }] : prev;
      }
      const next = [...prev];
      const q = (next[idx].quantity || 0) + delta;
      if (q <= 0) return next.filter((_, i) => i !== idx);
      next[idx] = { ...next[idx], quantity: q };
      return next;
    });
  }, []);
  const carQty = (car_type) =>
    selectedCars.find((c) => c.car_type === car_type)?.quantity || 0;
  const totalCarQty = selectedCars.reduce((s, c) => s + (c.quantity || 0), 0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cars");
        setCars(Array.isArray(data) ? data : []);
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // -----------------------------------------------------------------
  // City / destination chips (spec 2026-07-24 §EditParity). Same chip
  // set the AddRouteModal exposes, so admins have identical UX between
  // "add" and "edit" flows.
  // -----------------------------------------------------------------
  const PICKUP_CITIES = useMemo(() => [
    "مكة المكرمة", "جدة", "الطائف", "المدينة المنورة",
    "محطة قطار مكة", "محطة قطار المدينة", "مطار المدينة المنورة", "الفندق",
  ], []);
  const DEST_EXTRAS = useMemo(() => [
    "مزارات مكة المكرمة", "مزارات المدينة المنورة", "جولة سياحية في المدينة المنورة",
  ], []);

  // A chip is considered "active" when the current input already starts
  // with (or contains as substring) the chip's city label — so admins can
  // see at a glance which city was auto-detected from a landmark string
  // like "الطائف المطار" or "مكة المكرمة بيوت الجزيرة".
  const chipActive = (input, chip) => {
    const s = (input || "").trim();
    return s === chip || s.startsWith(chip + " ") || s.startsWith(chip);
  };

  // Clicking a chip replaces ONLY the leading city portion, keeping any
  // landmark suffix (e.g. clicking "جدة" on "مكة المكرمة بيوت الجزيرة"
  // yields "جدة بيوت الجزيرة"). If no chip currently matches, we replace
  // the whole value with the chip.
  const applyChip = (field, chip) => {
    const cur = (form[field] || "").trim();
    const match = [...PICKUP_CITIES, ...DEST_EXTRAS].find((c) => chipActive(cur, c));
    const next = match ? cur.replace(match, chip).trim() : chip;
    set(field, next);
  };

  // -----------------------------------------------------------------
  // Live price preview (spec 2026-07-24 §EditParity).
  //
  // Mirrors AddRouteModal: whenever pickup / destination / car_type / qty /
  // trip_date change, we POST to `/api/admin/bookings/{id}/preview-route-price`
  // and expose `previewPrice` + `hasPricingRule`. The admin can:
  //   • see the "correct" price computed from route_rules,
  //   • copy it into the manual `total_price` field with one click.
  // The preview is debounced (300ms) to avoid spamming while typing.
  // -----------------------------------------------------------------
  const [preview, setPreview] = useState({ price: null, hasRule: true, loading: false });
  useEffect(() => {
    const pickup = (form.pickup_location || "").trim();
    const destination = (form.destination || "").trim();
    if (!pickup || !destination || selectedCars.length === 0) {
      setPreview({ price: null, hasRule: true, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/admin/bookings/${booking.id}/preview-route-price`,
          {
            pickup_location: pickup,
            destination,
            trip_date: form.trip_date || undefined,
            cars: selectedCars,
          },
          { signal: controller.signal },
        );
        const p = Number(data?.route_total || 0);
        const hasRule = !!data?.has_pricing_rule;
        setPreview({ price: p, hasRule, loading: false });
        if (hasRule && p > 0) {
          setForm((f) => ({ ...f, total_price: String(p) }));
        }
      } catch (_e) {
        setPreview({ price: null, hasRule: true, loading: false });
      }
    }, 300);
    return () => { clearTimeout(t); controller.abort(); };
  }, [booking.id, form.pickup_location, form.destination, selectedCars, form.trip_date]);

  // Spec 2026-07-22 v3.2 §Train-Auto-Detect: when pickup OR destination looks
  // like a Haramain / train station, the flight fields are meaningless. We:
  //   1. auto-set flight_airline = "قطار"
  //   2. clear flight_number
  //   3. disable both inputs (bg + cursor-not-allowed)
  //   4. show a subtle info banner
  // The detection is LIVE — swapping pickup back to "جدة" re-enables them and
  // clears the "قطار" placeholder so the admin can type a real airline.
  const isTrain = useMemo(
    () => isTrainStation(form.pickup_location) || isTrainStation(form.destination),
    [form.pickup_location, form.destination],
  );
  const _prevIsTrain = useRef(isTrain);
  useEffect(() => {
    if (isTrain && !_prevIsTrain.current) {
      // JUST switched INTO a train route → stamp "قطار" + clear number
      setForm((f) => ({ ...f, flight_airline: "قطار", flight_number: "" }));
    } else if (!isTrain && _prevIsTrain.current) {
      // JUST switched OUT of a train route → clear the "قطار" stamp so the
      // admin can enter a real airline. Only clear when it's the auto-stamp.
      setForm((f) => (f.flight_airline === "قطار" ? { ...f, flight_airline: "" } : f));
    }
    _prevIsTrain.current = isTrain;
  }, [isTrain]);

  const submit = async () => {
    // Build minimal PATCH payload — only send fields that differ from booking.
    const payload = {};
    const trim = (s) => (typeof s === "string" ? s.trim() : s);

    // Validate phones if they were changed and non-empty.
    if (form.client_phone && String(form.client_phone).replace(/\D/g, "").length > 3) {
      const c = validatePhone(form.client_phone);
      if (!c.ok) { toast.error(c.reason); return; }
    }
    if (form.client_phone_2 && String(form.client_phone_2).replace(/\D/g, "").length > 3) {
      const c = validatePhone(form.client_phone_2);
      if (!c.ok) { toast.error(`رقم إضافي: ${c.reason}`); return; }
    }

    // Status
    if (form.status !== booking.status) payload.status = form.status;

    // Client
    if (trim(form.client_name) !== trim(booking.client_name || booking.user_name || "")) payload.client_name = trim(form.client_name);
    if (trim(form.client_phone) !== trim(booking.client_phone || booking.user_phone || "")) payload.client_phone = trim(form.client_phone);
    if (trim(form.client_phone_2) !== trim(booking.client_phone_alt || booking.client_phone_2 || "")) payload.client_phone_2 = trim(form.client_phone_2);

    // Trip
    if (trim(form.pickup_location) && trim(form.pickup_location) !== trim(booking.pickup_location || "")) payload.pickup_location = trim(form.pickup_location);
    if (trim(form.destination) && trim(form.destination) !== trim(booking.destination || booking.dropoff_location || "")) payload.destination = trim(form.destination);
    if (form.trip_date && form.trip_date !== booking.trip_date) payload.trip_date = form.trip_date;
    if (form.trip_time && form.trip_time !== booking.trip_time) payload.trip_time = form.trip_time;

    // Flight (spec 2026-07-22 v3.2 §Train): when the route is train-based,
    // ALWAYS force flight_airline="قطار" and flight_number=null on save so the
    // backend never keeps stale FZ 873 values from a previous edit.
    if (isTrain) {
      if ((booking.flight_number || "") !== "") payload.flight_number = null;
      if ((booking.flight_airline || "") !== "قطار") payload.flight_airline = "قطار";
    } else {
      if (trim(form.flight_number) !== trim(booking.flight_number || "")) payload.flight_number = trim(form.flight_number);
      if (trim(form.flight_airline) !== trim(booking.flight_airline || "")) payload.flight_airline = trim(form.flight_airline);
    }

    // Departure time overrides — send only on change. "" clears the override server-side.
    const _origDepC = booking.departure_time_client || "";
    const _origDepD = booking.departure_time_driver || "";
    const _newDepC = trim(form.departure_time_client || "");
    const _newDepD = trim(form.departure_time_driver || "");
    if (_newDepC !== _origDepC) payload.departure_time_client = _newDepC;
    if (_newDepD !== _origDepD) payload.departure_time_driver = _newDepD;

    // Cars — multi-car list (spec 2026-07-24 v8 §MultiCarEdit).
    // Send cars whenever the list changed (add/remove/qty). We compare
    // against the initial snapshot to keep the payload minimal.
    const _serializeCars = (arr) =>
      (arr || [])
        .map((c) => `${c.car_type}:${Number(c.quantity) || 0}`)
        .sort()
        .join("|");
    const _origCarsKey = _serializeCars(_initialCars);
    const _newCarsKey = _serializeCars(selectedCars);
    if (_newCarsKey !== _origCarsKey && selectedCars.length > 0) {
      payload.cars = selectedCars.map((c) => ({
        car_type: c.car_type,
        quantity: Math.max(1, Number(c.quantity) || 1),
      }));
      // If the booking has a routes[] array, patch route 0's cars too so the
      // per-route flat rows in the bookings table pick up the change.
      if (Array.isArray(booking.routes) && booking.routes.length > 0) {
        payload.routes = booking.routes.map((r, i) =>
          i === 0
            ? {
                ...r,
                cars: payload.cars,
              }
            : r,
        );
      }
    }
    const newPax = parseInt(form.passengers, 10);
    if (!Number.isNaN(newPax) && newPax > 0 && newPax !== booking.passengers) payload.passengers = newPax;
    const totalStr = String(form.total_price).trim();
    if (totalStr !== "") {
      const t = parseFloat(totalStr);
      if (!Number.isNaN(t) && t >= 0 && t !== Number(booking.total_price)) payload.total_price = t;
    }

    // 2026-02-26 §PerRouteFinancials: عمولة المندوب و عميل كاش لم يعودا
    // يُرسلان من هذه النافذة (انتقلا لمستوى المسار). إرسال 0 من هنا كان
    // سيمسح القيم القديمة على مستوى الحجز والتي تُستخدم كـ fallback في
    // التقارير للحجوزات القديمة. لذا نتجنّب إرسالهما نهائياً.

    // Notes
    if ((form.notes || "") !== (booking.notes || "")) payload.notes = form.notes;

    if (Object.keys(payload).length === 0) {
      toast.info("لا توجد تعديلات لحفظها");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/bookings/${booking.id}`, payload);
      toast.success("تم حفظ التغييرات ✓");
      onSaved(data);
    } catch (err) {
      toast.error("فشل الحفظ: " + formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const STATUSES = [
    { key: "pending", label: "قيد المراجعة", cls: "bg-orange-500/15 border-orange-500/60 text-orange-300" },
    { key: "confirmed", label: "مؤكد", cls: "bg-green-500/15 border-green-500/60 text-green-400" },
    { key: "completed", label: "مكتمل", cls: "bg-primary/15 border-primary/60 text-primary" },
    { key: "rejected", label: "مرفوض", cls: "bg-destructive/15 border-destructive/60 text-destructive" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      dir="rtl"
      onClick={onClose}
      data-testid="edit-booking-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-primary/20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/40 flex items-center justify-center">
              <PencilSimple size={18} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">تعديل الحجز</h2>
              <div className="text-white/50 text-xs num" dir="ltr">#{booking.seq_number || booking.booking_number || booking.id?.slice(0, 8)}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center"
            data-testid="edit-booking-close"
          >
            <XCircle size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Status section removed (2026-07 spec): bookings are always مؤكد. */}

          {/* Section 2 — Client */}
          <Section title="👤 بيانات العميل">
            <div className="space-y-3">
              <Row label="اسم العميل">
                <input type="text" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="mz-input" data-testid="edit-client-name" />
              </Row>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Row label="رقم الجوال">
                  <PhoneInput
                    value={String(form.client_phone || "").replace(/\D/g, "")}
                    onChange={(v) => set("client_phone", v ? `+${v}` : "")}
                    testId="edit-client-phone"
                    placeholder="رقم جوال العميل"
                  />
                </Row>
                <Row label="رقم جوال إضافي">
                  <PhoneInput
                    value={String(form.client_phone_2 || "").replace(/\D/g, "")}
                    onChange={(v) => set("client_phone_2", v ? `+${v}` : "")}
                    testId="edit-client-phone-2"
                    placeholder="رقم جوال إضافي"
                  />
                </Row>
              </div>
            </div>
          </Section>

          {/* Section 3 — Trip */}
          <Section title="✈️ تفاصيل الرحلة">
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Row label="من (نقطة الانطلاق)">
                  <input
                    type="text"
                    value={form.pickup_location}
                    onChange={(e) => set("pickup_location", e.target.value)}
                    placeholder="اكتب مدينة + معلَم (مثال: مكة المكرمة بيوت الجزيرة)"
                    className="mz-input"
                    data-testid="edit-pickup"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {PICKUP_CITIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => applyChip("pickup_location", c)}
                        data-testid={`edit-pickup-chip-${c}`}
                        className={
                          "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " +
                          (chipActive(form.pickup_location, c)
                            ? "bg-primary/25 border-primary text-primary"
                            : "bg-white/5 border-white/15 text-white/70 hover:bg-white/10")
                        }
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </Row>
                <Row label="إلى (الوجهة)">
                  <input
                    type="text"
                    value={form.destination}
                    onChange={(e) => set("destination", e.target.value)}
                    placeholder="اكتب مدينة + معلَم (مثال: الطائف المطار)"
                    className="mz-input"
                    data-testid="edit-destination"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[...PICKUP_CITIES, ...DEST_EXTRAS].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => applyChip("destination", c)}
                        data-testid={`edit-destination-chip-${c}`}
                        className={
                          "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " +
                          (chipActive(form.destination, c)
                            ? "bg-primary/25 border-primary text-primary"
                            : "bg-white/5 border-white/15 text-white/70 hover:bg-white/10")
                        }
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </Row>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Row label="التاريخ">
                  <CustomDatePicker
                    value={form.trip_date}
                    onChange={(v) => set("trip_date", v || "")}
                    placeholder="YYYY-MM-DD"
                    testId="edit-date"
                  />
                </Row>
                <Row label="الوقت">
                  <CustomTimePicker
                    value={form.trip_time}
                    onChange={(v) => set("trip_time", v || "")}
                    placeholder="HH:MM"
                    testId="edit-time"
                    intervals={1}
                  />
                </Row>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Row label="رقم الرحلة">
                  <input
                    type="text"
                    value={isTrain ? "" : form.flight_number}
                    onChange={(e) => set("flight_number", e.target.value)}
                    className={"mz-input " + (isTrain ? "opacity-50 cursor-not-allowed bg-white/5" : "")}
                    dir="ltr"
                    placeholder={isTrain ? "—" : "SV1052"}
                    disabled={isTrain}
                    data-testid="edit-flight-number"
                  />
                </Row>
                <Row label="شركة الطيران">
                  <input
                    type="text"
                    value={isTrain ? "قطار" : form.flight_airline}
                    onChange={(e) => set("flight_airline", e.target.value)}
                    className={"mz-input " + (isTrain ? "opacity-50 cursor-not-allowed bg-white/5" : "")}
                    placeholder="الخطوط السعودية"
                    disabled={isTrain}
                    data-testid="edit-flight-airline"
                  />
                </Row>
              </div>
              {isTrain && (
                <div
                  data-testid="train-flight-notice"
                  className="rounded-lg bg-primary/10 border border-primary/40 text-primary text-[12px] font-semibold px-3 py-2"
                >
                  🚄 حقول الطيران معطّلة تلقائياً — الرحلة عبر القطار
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Row label="تحرك العميل (HH:MM)">
                  <CustomTimePicker
                    value={form.departure_time_client}
                    onChange={(v) => set("departure_time_client", v || "")}
                    placeholder="HH:MM"
                    testId="edit-departure-client"
                    intervals={1}
                  />
                </Row>
                <Row label="تحرك السائق (HH:MM)">
                  <CustomTimePicker
                    value={form.departure_time_driver}
                    onChange={(v) => set("departure_time_driver", v || "")}
                    placeholder="HH:MM"
                    testId="edit-departure-driver"
                    intervals={1}
                  />
                </Row>
              </div>
              <div className="text-[11px] text-white/50 -mt-1">
                💡 اترك الحقلين فارغين لاستخدام الحساب التلقائي حسب المسار (مكة/جدة/المدينة/الطائف ← مطار أو محطة).
              </div>
            </div>
          </Section>

          {/* Section 4 — Cars & Price (multi-car list) */}
          <Section title="🚗 السيارات والسعر">
            <div className="space-y-3">
              <div>
                <label className="text-white/80 text-xs font-semibold mb-1 block flex items-center justify-between">
                  <span>نوع السيارة والعدد</span>
                  {totalCarQty > 0 && (
                    <span className="text-primary text-[11px] num" dir="ltr">
                      إجمالي: {fmtNum(totalCarQty)}
                    </span>
                  )}
                </label>
                <div className="space-y-2" data-testid="edit-cars-list">
                  {cars.length === 0 && <div className="text-white/40 text-xs">جاري التحميل…</div>}
                  {cars.map((c) => {
                    const key = c.id || c.car_type;
                    const qty = carQty(key);
                    const active = qty > 0;
                    return (
                      <div
                        key={key}
                        data-testid={`edit-car-row-${key}`}
                        className={
                          "flex items-center justify-between gap-3 px-3 py-2 rounded-xl border transition " +
                          (active
                            ? "bg-primary/15 border-primary/60"
                            : "bg-white/5 border-white/10")
                        }
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className={"font-bold text-sm " + (active ? "text-primary" : "text-white/85")}>
                            {c.name_ar || c.car_type || key}
                          </span>
                          {c.capacity && (
                            <span className="text-white/50 text-[10px] num" dir="ltr">
                              ({fmtNum(c.capacity)} راكب)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => carSet(key, -1)}
                            disabled={qty <= 0}
                            data-testid={`edit-car-minus-${key}`}
                            className="w-8 h-8 rounded-full border border-white/20 text-white text-lg font-bold hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            −
                          </button>
                          <span
                            data-testid={`edit-car-qty-${key}`}
                            className={"num min-w-[24px] text-center font-extrabold " + (active ? "text-primary" : "text-white/40")}
                            dir="ltr"
                          >
                            {fmtNum(qty)}
                          </span>
                          <button
                            type="button"
                            onClick={() => carSet(key, +1)}
                            data-testid={`edit-car-plus-${key}`}
                            className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-lg font-bold hover:bg-primary/90"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Live price preview — spec 2026-07-24 v2 §AutoPrice.
                  The total_price field auto-syncs with this value, so the
                  admin only clicks "حفظ" — no extra "use this price" step. */}
              {(preview.price != null || preview.loading) && (
                <div
                  data-testid="edit-price-preview"
                  className={
                    "rounded-xl border p-3 flex items-center justify-between gap-3 " +
                    (preview.hasRule
                      ? "bg-[#22C55E]/10 border-[#22C55E]/40"
                      : "bg-[#F59E0B]/10 border-[#F59E0B]/40")
                  }
                >
                  <div className="flex items-center gap-2 text-sm flex-1">
                    <span className={preview.hasRule ? "text-[#4ADE80]" : "text-[#FBBF24]"}>
                      {preview.hasRule ? "✅" : "⚠️"}
                    </span>
                    <span className="text-white/85 font-semibold">
                      {preview.hasRule
                        ? "السعر مُحدَّث تلقائياً من قواعد التسعير الرسمية"
                        : "لا توجد قاعدة تسعير مطابقة — أدخل السعر يدوياً"}
                    </span>
                    {preview.hasRule && preview.price != null && (
                      <span className="num font-extrabold text-[#4ADE80] text-base mr-auto" dir="ltr">
                        {preview.loading ? "…" : fmtNum(preview.price)} <span className="text-white/50 text-xs">ر.س</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Row label="عدد الركاب">
                  <input type="number" min="1" max="500" value={form.passengers} onChange={(e) => set("passengers", e.target.value)} className="mz-input num" dir="ltr" data-testid="edit-passengers" />
                </Row>
                <Row label="السعر الإجمالي (ر.س)" hint="يُحدَّث تلقائياً من قواعد التسعير — عدّله يدوياً فقط إذا كانت هناك ظروف خاصة">
                  <input type="number" min="0" step="0.01" value={form.total_price} onChange={(e) => set("total_price", e.target.value)} className="mz-input num" dir="ltr" placeholder="—" data-testid="edit-total-price" />
                </Row>
              </div>
              {/* 2026-02-26 §PerRouteFinancials: عمولة المندوب و عميل كاش
                  انتقلا لمستوى المسار. المشرف يعدّلهما عبر زر ✏️ لكل مسار. */}
              <div
                data-testid="edit-financials-moved-notice"
                className="mt-3 rounded-xl border border-[#3B82F6]/50 bg-[#3B82F6]/10 text-[#93C5FD] text-[12.5px] px-3 py-2.5 flex items-start gap-2"
              >
                <span aria-hidden className="text-lg leading-none">ℹ️</span>
                <span className="leading-relaxed">
                  عمولة المندوب وعميل كاش أصبحت لكل مسار على حِدة — عدّلها من
                  زر <span className="font-bold">✏️</span> على المسار المعني في
                  قسم "المسارات".
                </span>
              </div>
            </div>
          </Section>

          {/* Section 5 — Notes */}
          <Section title="📝 ملاحظات">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="mz-input resize-none"
              placeholder="أي ملاحظات خاصة بالحجز…"
              data-testid="edit-notes"
            />
          </Section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-primary/20 bg-[#0B1A3A]/40">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/5 text-sm" data-testid="edit-booking-cancel">
            إلغاء
          </button>
          <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 rounded-lg bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-40 inline-flex items-center gap-2 text-sm" data-testid="edit-booking-save">
            <CheckCircle size={14} weight="bold" /> {saving ? "جاري الحفظ…" : "حفظ التغييرات"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-primary font-bold text-sm mb-2 pb-1 border-b border-primary/20">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <label className="text-white/80 text-xs font-semibold mb-1 block">{label}</label>
      {children}
      {hint && <div className="text-white/40 text-[11px] mt-1">{hint}</div>}
    </div>
  );
}

// ============================================================================
// Admin — Car Upgrade Modal
// ============================================================================
export function UpgradeCarModal({ booking, onClose, onSaved }) {
  const [cars, setCars] = useState([]);
  const [newCarType, setNewCarType] = useState("");
  const [keepPrice, setKeepPrice] = useState(true);
  const [newPrice, setNewPrice] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cars");
        setCars(Array.isArray(data) ? data : []);
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  const currentQty = (booking.cars && booking.cars[0]?.quantity) || 1;
  const currentPrice = Number(booking.total_price || 0);
  const availableCars = cars.filter((c) => (c.id || c.car_type) !== booking.car_type);

  const submit = async () => {
    if (!newCarType) { toast.error("اختر نوع السيارة الجديد"); return; }
    let priceVal = undefined;
    if (!keepPrice) {
      const p = parseFloat(String(newPrice).trim());
      if (!Number.isNaN(p) && p >= 0) priceVal = p;
    }
    setSaving(true);
    try {
      const payload = {
        new_car_type: newCarType,
        keep_original_price: !!keepPrice,
        new_price: priceVal,
        reason: reason.trim() || undefined,
      };
      // PER-ROUTE / PER-CAR upgrade (2026-02): forwarded when the modal is
      // opened from a specific car row.
      if (booking._upgradeRouteIdx !== undefined && booking._upgradeRouteIdx !== null) {
        payload.route_idx = booking._upgradeRouteIdx;
      }
      if (booking._upgradeCarIdx !== undefined && booking._upgradeCarIdx !== null) {
        payload.car_idx = booking._upgradeCarIdx;
      }
      const { data } = await api.post(`/admin/bookings/${booking.id}/upgrade-car`, payload);
      toast.success("تمت الترقية بنجاح ✓");
      onSaved(data);
    } catch (err) {
      toast.error("فشل الترقية: " + formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      dir="rtl"
      onClick={onClose}
      data-testid="upgrade-car-modal"
    >
      <div
        className="bg-card border rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        style={{ borderColor: "rgba(0,225,255,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "rgba(0,225,255,0.2)" }}>
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(0,225,255,0.15)", border: "1px solid #00E1FF" }}
            >
              <ArrowUp size={18} weight="bold" color="#00E1FF" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">ترقية السيارة</h2>
              <div className="text-white/50 text-xs num" dir="ltr">#{booking.seq_number || booking.booking_number || booking.id?.slice(0, 8)}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/10 text-white/70 flex items-center justify-center" data-testid="upgrade-close">
            <XCircle size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Current car card */}
          <div className="rounded-xl border border-white/10 bg-[#0B1A3A]/70 p-3">
            <div className="text-white/60 text-xs mb-1">السيارة الحالية</div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-white font-bold text-base">
                {booking.car_name_ar || booking.car_type}
                <span className="text-white/50 text-sm num" dir="ltr"> × {currentQty}</span>
              </div>
              <div className="text-white/70 text-sm">
                السعر الحالي: <span className="text-primary font-bold num" dir="ltr">{fmtRiyal(currentPrice)}</span>
              </div>
            </div>
          </div>

          {/* Car type chips */}
          <div>
            <div className="text-white/80 text-xs font-semibold mb-2">نوع السيارة الجديد:</div>
            <div className="flex flex-wrap gap-2" data-testid="upgrade-car-chips">
              {availableCars.length === 0 && <div className="text-white/40 text-xs">جاري التحميل…</div>}
              {availableCars.map((c) => {
                const key = c.id || c.car_type;
                const active = newCarType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewCarType(key)}
                    data-testid={`upgrade-chip-${key}`}
                    className={`px-4 py-2 rounded-full border text-sm font-bold transition-all ${
                      active
                        ? "text-black border-transparent"
                        : "bg-white/5 border-white/20 text-white/80 hover:bg-white/10"
                    }`}
                    style={active ? { backgroundColor: "#00E1FF" } : undefined}
                  >
                    {c.name_ar || c.car_type || key}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Free upgrade toggle */}
          <label
            className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border"
            style={{
              background: keepPrice ? "rgba(0,225,255,0.10)" : "rgba(255,255,255,0.04)",
              borderColor: keepPrice ? "#00E1FF" : "rgba(255,255,255,0.15)",
            }}
          >
            <button
              type="button"
              onClick={() => setKeepPrice(!keepPrice)}
              data-testid="toggle-keep-price"
              className="w-6 h-6 rounded border-2 flex items-center justify-center shrink-0 transition"
              style={{
                backgroundColor: keepPrice ? "#00E1FF" : "transparent",
                borderColor: keepPrice ? "#00E1FF" : "rgba(255,255,255,0.4)",
              }}
            >
              {keepPrice && <CheckCircle size={14} weight="fill" color="#000" />}
            </button>
            <div className="flex-1">
              <div className="text-white font-bold text-sm">🎁 ترقية مجانية (نفس السعر)</div>
              <div className="text-white/60 text-xs mt-0.5">لا يتغير السعر — مجاملة للعميل</div>
            </div>
          </label>

          {/* New price input (only shown when keepPrice is false) */}
          {!keepPrice && (
            <div>
              <label className="text-white/80 text-xs font-semibold mb-1 block">السعر الجديد (ر.س)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="اتركه فارغاً لاستخدام السعر الأساسي للسيارة الجديدة"
                className="mz-input num"
                dir="ltr"
                data-testid="upgrade-new-price"
              />
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-white/80 text-xs font-semibold mb-1 block">سبب الترقية (اختياري)</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mz-input resize-none"
              placeholder="مثال: عميل مميز، تعويض عن تأخير…"
              data-testid="upgrade-reason"
            />
          </div>

          {/* Info footer */}
          <div
            className="text-xs px-3 py-2 rounded-lg flex items-start gap-2"
            style={{ background: "rgba(0,225,255,0.08)", border: "1px solid rgba(0,225,255,0.3)", color: "#8DE8FA" }}
          >
            <span className="shrink-0">ℹ️</span>
            <span>سيُسجَّل تاريخ الترقية والاسم الأصلي للسيارة، وستظهر شارة «مُرَقّى» للأدمن والشركة والسائق.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-[#0B1A3A]/40" style={{ borderColor: "rgba(0,225,255,0.2)" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-white/20 text-white/80 hover:bg-white/5 text-sm"
            data-testid="upgrade-cancel"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !newCarType}
            className="px-5 py-2 rounded-lg text-black font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 text-sm"
            style={{ backgroundColor: "#00E1FF", boxShadow: saving ? "none" : "0 4px 14px rgba(0,225,255,0.4)" }}
            data-testid="upgrade-submit"
          >
            <ArrowUp size={14} weight="bold" /> {saving ? "جاري الترقية…" : "تأكيد الترقية"}
          </button>
        </div>
      </div>
    </div>
  );
}

